/**
 * Centralized grade utility — single source of truth for letter grades.
 *
 * TWO scales live here, one per baseline family:
 *
 * 1. GRADE_BOUNDARIES (international-calibrated) — for markups measured
 *    against national AVERAGE baselines: scenario blended markups,
 *    per-code markups, investigator tables.
 *    A: ≤ -70% (charging ≤ 30% of national avg — near-international)
 *    B: -70…-35 · C: -35…-5 · D: -5…+30 (average US hospital) · F: > +30
 *
 * 2. HOSPITAL_GRADE_BOUNDARIES — for the hospital-level overall metric,
 *    which since the 2026-07 resilient-averages upgrade is the
 *    MEDIAN-baseline markup (median_gross_markup_percentage — what
 *    overall_rank_gross is ordered by). Median baselines sit lower than
 *    means, so the same hospital scores a higher % against them; these
 *    thresholds are quantile-matched to the old avg scale so national
 *    grade shares are preserved (A 2.7%, B 27%, C 32%, D 19%, F 19%).
 *
 * Pick the scale that matches the metric being graded — grading a
 * median-baseline number on the avg scale (or vice versa) shifts every
 * hospital about one letter.
 */

export const GRADE_BOUNDARIES = {
  A: -70,
  B: -35,
  C: -5,
  D: 30,
  // F: everything above D
} as const;

/** Hospital-level scale (median-baseline metric). Quantile-matched 2026-07. */
export const HOSPITAL_GRADE_BOUNDARIES = {
  A: -45,
  B: 20,
  C: 90,
  D: 175,
  // F: everything above D
} as const;

export type LetterGrade = 'A' | 'B' | 'C' | 'D' | 'F';

export function getGrade(markup: number): LetterGrade {
  if (markup <= GRADE_BOUNDARIES.A) return 'A';
  if (markup <= GRADE_BOUNDARIES.B) return 'B';
  if (markup <= GRADE_BOUNDARIES.C) return 'C';
  if (markup <= GRADE_BOUNDARIES.D) return 'D';
  return 'F';
}

/** Grade a hospital's overall (median-baseline) markup. */
export function getHospitalGrade(markup: number): LetterGrade {
  if (markup <= HOSPITAL_GRADE_BOUNDARIES.A) return 'A';
  if (markup <= HOSPITAL_GRADE_BOUNDARIES.B) return 'B';
  if (markup <= HOSPITAL_GRADE_BOUNDARIES.C) return 'C';
  if (markup <= HOSPITAL_GRADE_BOUNDARIES.D) return 'D';
  return 'F';
}

export function gradeColor(grade: string): string {
  switch (grade) {
    case 'A': return '#22c55e';
    case 'B': return '#84cc16';
    case 'C': return '#eab308';
    case 'D': return '#f97316';
    case 'F': return '#ef4444';
    default: return '#888';
  }
}

/** Tailwind-friendly class tokens for grade coloring */
export function gradeTwClasses(grade: string): { color: string; bg: string } {
  switch (grade) {
    case 'A': return { color: 'text-green-400', bg: 'bg-green-500/20' };
    case 'B': return { color: 'text-lime-400', bg: 'bg-lime-500/20' };
    case 'C': return { color: 'text-yellow-400', bg: 'bg-yellow-500/20' };
    case 'D': return { color: 'text-orange-400', bg: 'bg-orange-500/20' };
    case 'F': return { color: 'text-red-400', bg: 'bg-red-500/20' };
    default: return { color: 'text-muted-foreground', bg: 'bg-muted/50' };
  }
}

export interface GradeInfo {
  grade: LetterGrade;
  color: string;
  twColor: string;
  twBg: string;
}

export function getGradeInfo(markup: number | undefined | null): GradeInfo & { grade: LetterGrade | 'N/A' } {
  if (markup == null) {
    return { grade: 'N/A' as any, color: '#888', twColor: 'text-muted-foreground', twBg: 'bg-muted/50' };
  }
  const grade = getGrade(markup);
  const tw = gradeTwClasses(grade);
  return { grade, color: gradeColor(grade), twColor: tw.color, twBg: tw.bg };
}

/** getGradeInfo for the hospital-level (median-baseline) metric. */
export function getHospitalGradeInfo(markup: number | undefined | null): GradeInfo & { grade: LetterGrade | 'N/A' } {
  if (markup == null) {
    return { grade: 'N/A' as any, color: '#888', twColor: 'text-muted-foreground', twBg: 'bg-muted/50' };
  }
  const grade = getHospitalGrade(markup);
  const tw = gradeTwClasses(grade);
  return { grade, color: gradeColor(grade), twColor: tw.color, twBg: tw.bg };
}

/** Color hex for markup badges (slightly different palette for inline badges) */
export function markupBadgeColor(markup: number): string {
  if (markup <= GRADE_BOUNDARIES.A) return '#16a34a';
  if (markup <= GRADE_BOUNDARIES.B) return '#65a30d';
  if (markup <= GRADE_BOUNDARIES.C) return '#ca8a04';
  if (markup <= GRADE_BOUNDARIES.D) return '#ea580c';
  return '#dc2626';
}

/** Marker-style color for map pins (scenario mode) */
export function scenarioMarkerColor(markup: number): string {
  if (markup <= GRADE_BOUNDARIES.C) return '#16a34a'; // green for A/B/C
  if (markup <= GRADE_BOUNDARIES.D) return '#eab308'; // yellow for D
  if (markup <= 150) return '#ea580c'; // orange
  return '#dc2626'; // red for extreme
}

/** SQL CASE expression for use in Python/server scripts.
 *  Hospital-level scale on the median-baseline metric (falls back to the
 *  mean-baseline column while medians backfill). */
export const GRADE_SQL_CASE = `
    CASE
      WHEN COALESCE(hrs.median_gross_markup_percentage, hrs.avg_gross_markup_percentage) IS NULL THEN 'N/A'
      WHEN COALESCE(hrs.median_gross_markup_percentage, hrs.avg_gross_markup_percentage) <= -45 THEN 'A'
      WHEN COALESCE(hrs.median_gross_markup_percentage, hrs.avg_gross_markup_percentage) <= 20  THEN 'B'
      WHEN COALESCE(hrs.median_gross_markup_percentage, hrs.avg_gross_markup_percentage) <= 90  THEN 'C'
      WHEN COALESCE(hrs.median_gross_markup_percentage, hrs.avg_gross_markup_percentage) <= 175 THEN 'D'
      ELSE 'F'
    END AS grade
`;
